import React, { useState, useEffect, useRef } from 'react';
import {
  Container, Row, Col, Card, Button, Form, Alert, ButtonGroup
} from 'react-bootstrap';
import SectionContainer from '../../components/UI/SectionContainer';
import Spinner from '../../components/UI/Spinner';
import auditLogService from '../../services/auditLogService';
import { openAlert } from '../../utils/common';
import AuditLogListView from './AuditLogListView';
import AuditLogStatsView from './AuditLogStatsView';
import AuditLogDetailModal from './AuditLogDetailModal';

/**
 * 감사로그 모니터링 메인 페이지
 * UC-04 V2 감사로그 시스템 - TRE-01 준수 (읽기 전용)
 */
const AdminAuditLogPage = () => {
  // 상태 관리
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });

  // 필터 상태
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    category: '',
    severity: '',
    user_id: '',
    event_id: '',
    result: ''
  });

  // 모달 상태
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // 뷰 상태
  const [activeView, setActiveView] = useState('list'); // list, stats
  
  // 검색 트리거 (통계 뷰 강제 업데이트용)
  const [searchTrigger, setSearchTrigger] = useState(0);

  // 자동 새로고침
  const [autoRefresh, setAutoRefresh] = useState(false);
  const intervalRef = useRef(null);

  // 초기 로드
  useEffect(() => {
    // 기본 날짜 설정 (최근 7일)
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    
    setFilters(prev => ({
      ...prev,
      start_date: weekAgo.toISOString().split('T')[0],
      end_date: today.toISOString().split('T')[0]
    }));

    fetchAuditLogs(1);
  }, []);

  // 자동 새로고침
  useEffect(() => {
    if (autoRefresh && activeView === 'list') {
      intervalRef.current = setInterval(() => {
        fetchAuditLogs(pagination.currentPage);
      }, 30000); // 30초마다
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh, activeView, pagination.currentPage]);

  // 감사로그 목록 조회
  const fetchAuditLogs = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== '')
      );

      const result = await auditLogService.getAuditLogs(
        cleanFilters,
        page,
        pagination.itemsPerPage
      );

      if (result.success) {
        // data가 배열인지 확인
        const logsData = Array.isArray(result.data) ? result.data : [];
        setLogs(logsData);
        
        setPagination(result.pagination || {
          currentPage: page,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: 20
        });
      } else {
        setError(result.message);
        openAlert({ title: '오류', description: result.message });
      }
    } catch (err) {
      const errorMessage = err.message || '감사로그를 불러오는 중 오류가 발생했습니다.';
      setError(errorMessage);
      openAlert({ title: '오류', description: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  // 상세 정보 조회
  const handleShowDetail = async (log) => {
    try {
      setShowDetailModal(true);
      setDetailLoading(true);
      setSelectedLog(log);

      const result = await auditLogService.getAuditLogDetail(log.id);

      if (result.success) {
        setSelectedLog(result.data);
      } else {
        openAlert({ title: '오류', description: result.message });
      }
    } catch (err) {
      openAlert({ 
        title: '오류', 
        description: '상세 정보를 불러오는 중 오류가 발생했습니다.' 
      });
    } finally {
      setDetailLoading(false);
    }
  };

  // 필터 변경
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 검색 (필터 적용)
  const handleSearch = () => {
    if (activeView === 'list') {
      fetchAuditLogs(1);
    } else if (activeView === 'stats') {
      // 통계 뷰 강제 재조회
      setSearchTrigger(prev => prev + 1);
    }
  };

  // 필터 초기화
  const handleResetFilters = () => {
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    setFilters({
      start_date: weekAgo.toISOString().split('T')[0],
      end_date: today.toISOString().split('T')[0],
      category: '',
      severity: '',
      user_id: '',
      event_id: '',
      result: ''
    });
    
    // 통계 뷰에서 초기화 시 강제 재조회
    if (activeView === 'stats') {
      setSearchTrigger(prev => prev + 1);
    }
  };

  // 엑셀 다운로드
  const handleExportExcel = async () => {
    try {
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== '')
      );

      const result = await auditLogService.exportToExcel(cleanFilters);

      if (result.success) {
        openAlert({ title: '성공', description: result.message });
      } else {
        openAlert({ title: '오류', description: result.message });
      }
    } catch (err) {
      openAlert({ 
        title: '오류', 
        description: '엑셀 다운로드 중 오류가 발생했습니다.' 
      });
    }
  };

  // 페이지 변경
  const handlePageChange = (page) => {
    fetchAuditLogs(page);
  };

  // 뷰 변경
  const handleViewChange = (view) => {
    setActiveView(view);
  };

  return (
    <SectionContainer title="감사로그 모니터링" className="list">
        <Spinner visible={loading} />
      {/* 헤더 */}
      <Row className="mb-4">
        <Col>
          <p className="text-muted">
            시스템 활동 및 보안 이벤트 추적
          </p>
        </Col>
        <Col xs="auto">
          <ButtonGroup>
            <Button
              variant={activeView === 'list' ? 'primary' : 'outline-primary'}
              onClick={() => handleViewChange('list')}
            >
              목록
            </Button>
            <Button
              variant={activeView === 'stats' ? 'primary' : 'outline-primary'}
              onClick={() => handleViewChange('stats')}
            >
              통계
            </Button>
          </ButtonGroup>
        </Col>
      </Row>

      {/* 오류 메시지 */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 필터 */}
      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <strong>필터</strong>
            {activeView === 'list' && (
              <Form.Check
                type="switch"
                id="auto-refresh-switch"
                label="자동 새로고침 (30초)"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
            )}
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>시작일</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>종료일</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>카테고리</Form.Label>
                <Form.Select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                >
                  <option value="">전체</option>
                  <option value="ACCESS_CONTROL">접근 제어</option>
                  <option value="DATA_PROTECTION">데이터 보호</option>
                  <option value="SYSTEM_SECURITY">시스템 보안</option>
                  <option value="AUDIT_MONITORING">감사 모니터링</option>
                  <option value="USER_MANAGEMENT">사용자 관리</option>
                  <option value="BUSINESS_LOGIC">비즈니스 로직</option>
                  <option value="SECURITY_VIOLATION">보안 위반</option>
                  <option value="BIOMETRIC_AUTH">생체 인증</option>
                  <option value="MEDICAL_DATA">의료 데이터</option>
                  <option value="BIOMETRIC_MANAGEMENT">생체정보 관리</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>심각도</Form.Label>
                <Form.Select
                  value={filters.severity}
                  onChange={(e) => handleFilterChange('severity', e.target.value)}
                >
                  <option value="">전체</option>
                  <option value="CRITICAL">CRITICAL</option>
                  <option value="HIGH">HIGH</option>
                  <option value="MEDIUM">MEDIUM</option>
                  <option value="LOW">LOW</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>사용자 ID</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="사용자 ID 입력"
                  value={filters.user_id}
                  onChange={(e) => handleFilterChange('user_id', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>이벤트 ID</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="이벤트 ID 입력"
                  value={filters.event_id}
                  onChange={(e) => handleFilterChange('event_id', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>결과</Form.Label>
                <Form.Select
                  value={filters.result}
                  onChange={(e) => handleFilterChange('result', e.target.value)}
                >
                  <option value="">전체</option>
                  <option value="SUCCESS">SUCCESS</option>
                  <option value="FAILURE">FAILURE</option>
                  <option value="WARNING">WARNING</option>
                  <option value="BLOCKED">BLOCKED</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end gap-2">
              <Button
                variant="primary"
                className="mb-3"
                onClick={handleSearch}
                disabled={loading}
              >
                검색
              </Button>
              <Button
                variant="outline-secondary"
                className="mb-3"
                onClick={handleResetFilters}
              >
                초기화
              </Button>
              <Button
                variant="success"
                className="mb-3"
                onClick={handleExportExcel}
                disabled={loading}
              >
                엑셀
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* 컨텐츠 영역 */}
      {activeView === 'list' && (
        <AuditLogListView
          logs={logs}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          onShowDetail={handleShowDetail}
        />
      )}

      {activeView === 'stats' && (
        <AuditLogStatsView filters={filters} searchTrigger={searchTrigger} />
      )}

      {/* 상세 정보 모달 */}
      <AuditLogDetailModal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        log={selectedLog}
        loading={detailLoading}
      />
    </SectionContainer>
  );
};

export default AdminAuditLogPage;
